import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import FormInput, { FormTextArea } from "pages/FormInput";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addAppeal, editAppeal2 } from "services/complaintDetailService";
import { editComplaintReception } from "services/complaintService";
import { Appeal, ComplaintReception } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

const defaultData: Appeal = {
  id: undefined,
  parent_appeal_id: undefined,
  appeal_document: null,
  resolution_file: null,
  status: "pending",
  appeal_date: undefined,
  lower_org_unit: "",
  lower_admin_unit: "",
  appeal_summary: "",
  resolution_text: "",
  resolution_date: undefined,
  resolved_by: "",
  resolved_by_user_id: "",
  admin_unit_id: "",
  org_unit_id: "",
  departement_id: "",
  parent_appeal: undefined,
};

type Props = {
  complaintId?: number | string;
  compliant?: ComplaintReception;
  appealId?: number | string;
  appeal?: Appeal;
  onClose: (refetch: boolean) => void;
};

const AppealRequestForm: React.FC<Props> = ({
  complaintId,
  compliant,
  appealId,
  appeal,
  onClose,
}) => {
  const navigate = useNavigate();
  // const location = useLoዖcation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [formState, setFormState] = useState<Appeal>(defaultData);
  const [loading, setLoading] = useState(false);

  // useEffect(() => {
  //   const fetchStructure = async (
  //     admin_unit_id: string,
  //     org_unit_id: string,
  //   ) => {
  //     setLoading(true);
  //     const res = await adminOrgStructure(admin_unit_id, org_unit_id);
  //     if (res) {
  //       setFormState((prev) => ({
  //         ...prev,
  //         org_unit_id:
  //           res?.org_unit?.parent_unit !== undefined &&
  //           res?.org_unit?.parent_unit !== null
  //             ? String(res?.org_unit?.parent_unit)
  //             : "",
  //       }));
  //       setLoading(false);
  //     }
  //   };
  //   fetchStructure(
  //     compliant?.admin_unit_id ?? "",
  //     compliant?.org_unit_id ?? "",
  //   );
  // }, [compliant]);

  useEffect(() => {
    if (loginData) {
      setFormState((prev) => ({
        ...prev,
        org_unit_id: String(loginData.org_unit),
      }));
    }
  }, [loginData]);

  // File handlers
  const handleFileChange = (name: string, file: File | null) => {
    setFormState((prev) => ({
      ...prev,
      [name]: file,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  const validateForm = () => {
    if (!complaintId && !appealId) return "Complaint ID is Required.";
    if (!formState.appeal_date) return "Appeal Date is Required.";
    if (new Date(formState?.appeal_date) > new Date()) {
      return `Appeal Date must be on or before ${new Date().toLocaleDateString()}`;
    }
    if (
      formState?.resolution_date &&
      new Date(formState?.resolution_date) > new Date()
    ) {
      return `Resolution Date must be on or before ${new Date().toLocaleDateString()}`;
    }
    if (!formState.appeal_summary) return "Appeal Claim Summary is Required.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    const complaint_id = complaintId
      ? complaintId
      : appealId
        ? undefined
        : formState.complaint_id;

    addAppeal({
      ...formState,
      status: "pending",
      resolved_by_user_id: loginData?.user_id,
      resolved_by: loginData?.full_name,
      org_unit_id: loginData?.org_unit,
      admin_unit_id: loginData?.admin_unit,
      departement_id: loginData?.structure?.org_unit?.uu_id,
      // lower_org_unit: loginData?.lower_org_unit,
      // lower_admin_unit: loginData?.lower_admin_unit,
      complaints: complaint_id,
      complaint_id: complaint_id,
      parent_appeal_id: appealId,
      parent_appeal: appealId,
      application_number: appeal
        ? appeal?.application_number
        : compliant?.application_number,

      is_attorney_appeal_request:
        !loginData?.structure?.org_unit?.parent_unit &&
        !loginData?.structure?.admin_unit?.parent_unit
          ? true
          : false,
    })
      .then((data) => {
        if (complaintId) {
          editComplaintReception({
            id: complaintId,
            is_appeal: true,
          });
        } else if (appealId) {
          editAppeal2({
            id: appealId,
            is_appealed: true,
          });
        }

        showTooltip("Successfully Submitted!", "success");
        navigate("/appeal");
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div
        className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto"
        style={{ maxHeight: "max-content" }}
      >
        {formState?.org_unit_id && (
          <form onSubmit={handleSubmit}>
            <span className="text-[24px] font-semibold ">
              Register New Appeal
            </span>

            {compliant && (
              <div className="pt-6">
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-5 border border-blue-100">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                    Complaint Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 font-medium">
                        Application Number
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="font-semibold text-gray-900">
                          {compliant?.client_id ?? "-"}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 font-medium">
                        Resolved By
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="font-semibold text-gray-900">
                          {(compliant?.resolution_info &&
                            compliant?.resolution_info.length > 0 &&
                            compliant?.resolution_info[0]?.resolved_by) ||
                            "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mt-4">
              <FormInput
                label="Appeal Date *"
                type="date"
                value={
                  formState.appeal_date
                    ? new Date(formState.appeal_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                max={new Date().toISOString().split("T")[0]}
                onChange={handleChange}
                name="appeal_date"
              />
              <FormInput
                label="Resolution Date"
                type="date"
                value={
                  formState.resolution_date
                    ? new Date(formState.resolution_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                max={new Date().toISOString().split("T")[0]}
                onChange={handleChange}
                name="resolution_date"
              />
            </div>

            <div className="flex flex-col gap-4 mt-4">
              <FormTextArea
                label="Appeal Claim Summary *"
                value={formState.appeal_summary || ""}
                onChange={handleChange}
                name="appeal_summary"
                placeholder="Enter appeal claim summary"
                rows={4}
              />
              <FormTextArea
                label="Lower Administration Unit Resolution"
                value={formState.resolution_text || ""}
                onChange={handleChange}
                name="resolution_text"
                placeholder="Enter lower administration unit resolution"
                rows={4}
              />
              <FileUpload
                label="Appeal Claim Document"
                fileTypes="*"
                info="Upload Files"
                required={false}
                onChange={(file) => handleFileChange("appeal_document", file)}
              />
              <FileUpload
                label="Lower Administration Unit Resolution Document"
                fileTypes="*"
                info="Upload Files"
                required={false}
                onChange={(file) => handleFileChange("resolution_file", file)}
              />
            </div>
            <div className="flex flex-row mt-4 gap-3">
              <button
                type="reset"
                disabled={loading}
                className="flex-1 border border-[#212121]  py-3 text-[#747474]  cursor-pointer font-[500] text-base rounded-md hover:bg-[#D4D4D4]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                // className="flex items-center gap-2 border border-[#212121] bg-[#212121] w-full px-[76px] py-3 text-white  cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
                className="flex-1 py-3 items-center bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg flex justify-center"
                value={"Submit Appeal"}
              >
                {loading ? (
                  <>
                    <span className="animate-spin">{LoadingIcon}</span>
                    Processing...
                  </>
                ) : (
                  "Submit Appeal"
                )}
              </button>
            </div>
          </form>
        )}
        {/* {!formState?.org_unit_id && (
          <div className="mb-4 flex flex-col">
            <span className="text-[24px] font-semibold ">
              Register New Appeal
            </span>
            {loading ? <Loading /> : "Change admin unit to request appeal"}
          </div>
        )} */}
      </div>
    </div>
  );
};

export default AppealRequestForm;
